/*
 * Settings → Integrations → API keys. DESIGN-api-and-webhooks.md step 1 (Rob 25/10/2026,
 * layout A of `Claude outputs/api-mock-v1.html`).
 *
 * ⚠️ **NOT SUPERADMIN → API KEYS.** That page is our own provider credentials. These are the
 * company's keys to read its own data, made by its admins.
 *
 * ⚠️ **THE KEY IS SHOWN ONCE, FROM THE RESPONSE THAT MADE IT, AND NEVER AGAIN.** The server
 * keeps only a scrambled copy; the list has nothing but the first 12 characters. Closing the
 * "Copy it now" window throws the key away for good, which is why that window says so.
 *
 * ⚠️ **A KEY THAT STOPPED IS SHOWN, NOT HIDDEN.** If the admin who made it leaves or stops
 * being an admin, the key stops working (the API only ever says "not valid", to strangers).
 * This card is where the company's own admin finds out why — rule 9, diagnostics on screen.
 *
 * Step 3 (Rob 26/10/2026, `Claude outputs/api-write-mock-v1.html`): the window has a second
 * group, "What it can change", with one tick — "Book and approve leave" — and a warning once
 * it's ticked: the key acts AS the admin who makes it.
 *
 * Step 4 (Rob 26/10/2026, layout A of `Claude outputs/api-people-mock-v1.html`): a second change
 * tick, "Manage people and departments", and each change tick has its own warning right under it.
 *
 * Step 5 (Rob 26/10/2026, `Claude outputs/api-allowances-mock-v1.html`): a third, "Manage
 * allowances and leave types", laid out the same way. The ticks and words are `lib/apiKeys.js`'s.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Copy, ExternalLink, KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { apiKeysApi, apiErrorMessage } from '../lib/api';
import { formatDateGB } from '../lib/dates';
import {
  CHANGE_TICKS, CHANGE_WARNINGS, READ_TICKS, SCOPES, SCOPE_HINTS, canMake, usedWords,
} from '../lib/apiKeys';

const DEFAULT_TICKS = ['people', 'leave'];

export default function ApiKeysCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [making, setMaking] = useState(false);       // the "Make a key" window
  const [name, setName] = useState('');
  const [ticks, setTicks] = useState(DEFAULT_TICKS);
  const [busy, setBusy] = useState(null);            // 'make' | key id
  const [made, setMade] = useState(null);            // { name, key } — shown once
  const [confirmRevoke, setConfirmRevoke] = useState(null);

  const load = useCallback(() => apiKeysApi.list()
    .then((res) => setData(res.data))
    .catch(() => setData(null))
    .finally(() => setLoading(false)), []);

  useEffect(() => { load(); }, [load]);

  const words = data?.scope_words || {};

  const openMaker = () => {
    setName('');
    setTicks(DEFAULT_TICKS);
    setMaking(true);
  };

  const toggle = (scope, on) => setTicks((now) => (on
    ? SCOPES.filter((s) => s === scope || now.includes(s))
    : now.filter((s) => s !== scope)));

  const tickRow = (s) => (
    <div key={s} className="flex items-start gap-2">
      <Checkbox id={`api-scope-${s}`} checked={ticks.includes(s)}
                onCheckedChange={(on) => toggle(s, !!on)} data-testid={`api-scope-${s}`} />
      <div>
        <Label htmlFor={`api-scope-${s}`} className="text-sm cursor-pointer">{words[s] || s}</Label>
        <p className="text-xs text-muted-foreground">{SCOPE_HINTS[s]}</p>
      </div>
    </div>
  );

  const make = async () => {
    setBusy('make');
    try {
      const res = await apiKeysApi.make(name.trim(), ticks);
      setData(res.data);
      setMaking(false);
      setMade({ name: res.data.made.name, key: res.data.made.key });
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Couldn’t make the key.'));
    } finally {
      setBusy(null);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(made.key);
      toast.success('Copied.');
    } catch {
      toast.error('Couldn’t copy it — select the key and copy it by hand.');
    }
  };

  const revoke = async (k) => {
    setBusy(k.id);
    try {
      const res = await apiKeysApi.revoke(k.id);
      setData(res.data);
      setConfirmRevoke(null);
      toast.success(`“${k.name}” revoked. It stopped working straight away.`);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Couldn’t revoke the key.'));
    } finally {
      setBusy(null);
    }
  };

  const header = (
    <div className="flex items-start justify-between gap-3 mb-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 shrink-0 rounded-lg bg-primary/20 flex items-center justify-center">
          <KeyRound className="w-5 h-5 text-primary" aria-hidden="true" />
        </div>
        <div>
          <h3 className="font-heading font-bold">API keys</h3>
          <p className="text-sm text-muted-foreground">
            Let another system read your people and leave, book and approve leave, or keep your people up to date — for example, to show who’s off in a project tool
          </p>
          {/* Rob, 25/10/2026: the developer notes should be one click from here. A new tab, so
              the admin doesn't lose their place in Settings. */}
          <a href="/developers" target="_blank" rel="noopener noreferrer"
             className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
             data-testid="api-developer-notes">
            How to use the API (developer notes)
            <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
          </a>
        </div>
      </div>
      {data && (
        <Button size="sm" onClick={openMaker} disabled={(data.keys || []).length >= data.max_keys}
                data-testid="api-key-make">
          Make a key
        </Button>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="card-hive p-6" data-testid="integrations-api-keys-card">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="card-hive p-6" data-testid="integrations-api-keys-card">
        {header}
        <p className="text-xs text-muted-foreground">Couldn’t load your API keys. Please refresh the page.</p>
      </div>
    );
  }

  const keys = data.keys || [];

  return (
    <div className="card-hive p-6" data-testid="integrations-api-keys-card">
      {header}

      {keys.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="api-keys-none">
          No keys yet. Each key can only do what you tick when you make it, and you can revoke it at any time.
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {keys.map((k) => (
            <li key={k.id} className="py-3 flex items-start gap-3" data-testid={`api-key-${k.id}`}>
              <div className={`w-8 h-8 shrink-0 rounded-md flex items-center justify-center ${k.stopped ? 'bg-amber-500/15' : 'bg-green-500/15'}`}>
                <KeyRound className="w-4 h-4" aria-hidden="true" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">{k.name}</p>
                <p className="text-xs text-muted-foreground">
                  <span className="font-mono">{k.prefix}…</span>
                  {' · '}{k.summary}
                  {' · '}{usedWords(k.last_used_at)}{k.last_used_at && k.last_used_ip ? ` from ${k.last_used_ip}` : ''}
                </p>
                <p className="text-xs text-muted-foreground">
                  Made by {k.made_by}{k.made_at ? ` on ${formatDateGB(k.made_at)}` : ''}
                </p>
                {k.stopped && (
                  <p className="text-xs text-amber-800 mt-1 flex items-start gap-1" data-testid="api-key-stopped">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" aria-hidden="true" />
                    <span>Not working: {k.stopped}</span>
                  </p>
                )}
                {confirmRevoke === k.id && (
                  <div className="mt-2 p-3 rounded-md bg-red-50 border border-red-200 text-xs text-red-900"
                       data-testid="api-key-confirm">
                    <p className="mb-2">
                      Revoke “{k.name}”? Anything using it stops working straight away. This can’t be undone; you’d make a new key instead.
                    </p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="destructive" disabled={busy === k.id} onClick={() => revoke(k)}>
                        {busy === k.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Revoke it'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setConfirmRevoke(null)}>Keep it</Button>
                    </div>
                  </div>
                )}
              </div>
              {confirmRevoke !== k.id && (
                <Button size="sm" variant="outline" className="text-red-600 border-red-200"
                        onClick={() => setConfirmRevoke(k.id)} data-testid="api-key-revoke">
                  Revoke
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs text-muted-foreground mt-4">
        Whatever you tick, a key never sends leave reasons, decline notes, the names of private leave types, IP addresses or sign-in history.
        Making or revoking a key is listed in Settings → Security → Sign-ins.
        {' '}A key that can change leave or people does it as the admin who made it, and says so.
      </p>

      {/* Step 1 — name it, tick what it can read */}
      <Dialog open={making} onOpenChange={(o) => { if (!busy) setMaking(o); }}>
        <DialogContent className="max-w-md" data-testid="api-key-maker">
          <DialogHeader>
            <DialogTitle>Make an API key</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="api-key-name">Name, so you know what it’s for later</Label>
              <Input id="api-key-name" value={name} maxLength={80} placeholder="Hive feed"
                     onChange={(e) => setName(e.target.value)} data-testid="api-key-name" />
            </div>
            <div className="space-y-3">
              <p className="text-sm font-medium">What it can read</p>
              {READ_TICKS.map((s) => tickRow(s))}
            </div>
            <div className="space-y-3" data-testid="api-key-change-ticks">
              <p className="text-sm font-medium">What it can change</p>
              {CHANGE_TICKS.map((s) => (
                <React.Fragment key={s}>
                  {tickRow(s)}
                  {ticks.includes(s) && CHANGE_WARNINGS[s] && (
                    <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900"
                         data-testid={`api-key-${s}-warning`}>
                      <strong>{CHANGE_WARNINGS[s].strong}</strong> {CHANGE_WARNINGS[s].rest}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Never sent, whatever you tick: leave reasons, decline notes, time in lieu notes, private leave types’ real names, IP addresses, sign-in history.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setMaking(false)} disabled={busy === 'make'}>Cancel</Button>
              <Button onClick={make} disabled={!canMake(name, ticks) || busy === 'make'} data-testid="api-key-save">
                {busy === 'make' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Make the key'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Step 2 — copy it now. Closing throws it away; the window says so. */}
      <Dialog open={!!made} onOpenChange={(o) => { if (!o) setMade(null); }}>
        <DialogContent className="max-w-lg" data-testid="api-key-made">
          <DialogHeader>
            <DialogTitle>Your key for “{made?.name}”</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="font-mono text-sm bg-secondary rounded-md p-3 break-all select-all" data-testid="api-key-value">
              {made?.key}
            </div>
            <Button onClick={copy} size="sm"><Copy className="w-4 h-4 mr-1" aria-hidden="true" />Copy</Button>
            <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900">
              <strong>This is the only time you’ll see it.</strong> We keep a scrambled copy that can check it, but can’t show it again.
              If it’s lost, revoke it and make another.
            </div>
            <p className="text-xs text-muted-foreground">
              Anyone who has this key can do what you ticked, for the whole company. Treat it like a password.
              Programs send it as <span className="font-mono">Authorization: Bearer …</span> to <span className="font-mono break-all">{data.base_url}</span>.{' '}
              <a href="/developers" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                How to use it
              </a>.
            </p>
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setMade(null)} data-testid="api-key-done">I’ve copied it</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
